import type {
  TextHorizontalAlign,
  TextLineHeight,
  TextTypography,
  TextVerticalAlign,
  TypeStyle,
} from "@/document/model/text";

/**
 * Parse optional typography fields from a flat params object (create_text / set_typography).
 */
export function readOptionalTypographyFields(params: Record<string, unknown>): Partial<TypeStyle> {
  const fields: Record<string, unknown> = {};
  for (const key of [
    "fontFamily",
    "fontWeight",
    "fontSize",
    "letterSpacing",
    "horizontalAlign",
    "verticalAlign",
    "lineHeight",
  ] as const) {
    if (params[key] !== undefined) {
      fields[key] = params[key];
    }
  }
  if (Object.keys(fields).length === 0) {
    return {};
  }
  return readTypographyPatch(fields);
}

/** Parse a typography patch record (update_style.typography or flat field bag). */
export function readTypographyPatch(value: unknown): Partial<TextTypography> {
  const patch = requireRecord(value, "typography");
  const next: Partial<TextTypography> = {};

  if (patch.fontFamily !== undefined) {
    if (typeof patch.fontFamily !== "string" || !patch.fontFamily) {
      throw new Error("typography.fontFamily must be a non-empty string.");
    }
    next.fontFamily = patch.fontFamily;
  }
  if (patch.fontWeight !== undefined) {
    if (typeof patch.fontWeight !== "number" || !Number.isFinite(patch.fontWeight)) {
      throw new Error("typography.fontWeight must be a number.");
    }
    next.fontWeight = patch.fontWeight;
  }
  if (patch.fontSize !== undefined) {
    if (typeof patch.fontSize !== "number" || !Number.isFinite(patch.fontSize)) {
      throw new Error("typography.fontSize must be a number.");
    }
    next.fontSize = patch.fontSize;
  }
  if (patch.letterSpacing !== undefined) {
    if (typeof patch.letterSpacing !== "number" || !Number.isFinite(patch.letterSpacing)) {
      throw new Error("typography.letterSpacing must be a number.");
    }
    next.letterSpacing = patch.letterSpacing;
  }
  if (patch.horizontalAlign !== undefined) {
    const align = patch.horizontalAlign;
    if (align !== "LEFT" && align !== "CENTER" && align !== "RIGHT" && align !== "JUSTIFIED") {
      throw new Error('typography.horizontalAlign must be LEFT|CENTER|RIGHT|JUSTIFIED.');
    }
    next.horizontalAlign = align as TextHorizontalAlign;
  }
  if (patch.verticalAlign !== undefined) {
    const align = patch.verticalAlign;
    if (align !== "TOP" && align !== "CENTER" && align !== "BOTTOM") {
      throw new Error("typography.verticalAlign must be TOP|CENTER|BOTTOM.");
    }
    next.verticalAlign = align as TextVerticalAlign;
  }
  if (patch.lineHeight !== undefined) {
    next.lineHeight = parseLineHeight(patch.lineHeight);
  }
  return next;
}

export function mergeTypography(current: TextTypography, value: unknown): TextTypography {
  return { ...current, ...readTypographyPatch(value) };
}

export function summarizeTypography(style: TypeStyle | TextTypography): TextTypography {
  return {
    fontFamily: style.fontFamily,
    fontWeight: style.fontWeight,
    fontSize: style.fontSize,
    lineHeight: style.lineHeight,
    letterSpacing: style.letterSpacing,
    horizontalAlign: style.horizontalAlign,
    verticalAlign: style.verticalAlign,
  };
}

function parseLineHeight(value: unknown): TextLineHeight {
  if (value === "auto") {
    return "auto";
  }
  const record = requireRecord(value, "typography.lineHeight");
  const unit = requireString(record, "unit");
  const amount = requireNumber(record, "value");
  if (unit !== "px" && unit !== "percent") {
    throw new Error('typography.lineHeight.unit must be "px" or "percent".');
  }
  return { unit, value: amount };
}

function requireRecord(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${label} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function requireString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  if (typeof value !== "string") {
    throw new Error(`Missing string param "${key}".`);
  }
  return value;
}

function requireNumber(record: Record<string, unknown>, key: string): number {
  const value = record[key];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Missing number param "${key}".`);
  }
  return value;
}
